import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToggleBloqueoTramite } from '@/hooks/useToggleBloqueoTramite';
import type { Tramite } from '@/types';

interface BloqueoTramiteDialogProps {
  tramite: Pick<Tramite, 'id'> | null;
  isOpen: boolean;
  onClose: () => void;
  onBlocked?: (tramite: Tramite) => void;
}

export function BloqueoTramiteDialog({ tramite, isOpen, onClose, onBlocked }: BloqueoTramiteDialogProps) {
  const [motivo, setMotivo] = useState('');
  const { toggle, isSubmitting } = useToggleBloqueoTramite(onBlocked);

  useEffect(() => {
    if (isOpen) {
      setMotivo('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!tramite || !motivo.trim()) return;

    try {
      await toggle({ id: tramite.id, bloqueado: true, motivo: motivo.trim() });
      onClose();
    } catch {
      // El error ya se notifica en el hook
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] min-w-0 max-w-md">
        <DialogHeader>
          <DialogTitle>Bloquear escritura</DialogTitle>
          <DialogDescription>
            Indicá el motivo por el cual se bloquea esta escritura.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo del bloqueo"
          maxLength={1000}
          className="max-h-40 overflow-y-auto"
          disabled={isSubmitting}
          autoFocus
        />

        <DialogFooter className="gap-2 sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || motivo.trim() === ''}
          >
            Bloquear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BloqueoTramiteDialog;
